"use client";

import React, { useState } from "react";
import Link from "next/link";
import { 
  Building, 
  MapPin, 
  Calendar, 
  Car, 
  ShieldCheck, 
  Sparkles, 
  Award, 
  ArrowRight,
  Phone,
  Mail,
  KanbanSquare,
  TrendingUp,
  Clock,
  Compass
} from "lucide-react";
import RealEstateHero from "@/components/demos/real-estate/RealEstateHero";
import PropertyGrid from "@/components/demos/real-estate/PropertyGrid";
import SiteVisitModal from "@/components/demos/real-estate/SiteVisitModal";
import BrochureDownloadModal from "@/components/demos/real-estate/BrochureDownloadModal";
import { DemoCommercialBanner } from "@/components/demos/DemoCommercialBanner";
import { PropertyItem, FEATURED_PROPERTIES } from "@/lib/demos/propertiesData";

export default function RealEstateDemoWebsite({ currency = "INR" }: { currency?: "INR" | "USD" }) {
  const [cityFilter, setCityFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [priceFilter, setPriceFilter] = useState("ALL");

  const [activeSiteVisitProperty, setActiveSiteVisitProperty] = useState<PropertyItem | null>(null);
  const [isSiteVisitModalOpen, setIsSiteVisitModalOpen] = useState(false);

  const [activeBrochureProperty, setActiveBrochureProperty] = useState<PropertyItem | null>(null);
  const [isBrochureModalOpen, setIsBrochureModalOpen] = useState(false);

  const handleOpenSiteVisit = (prop?: PropertyItem) => {
    setActiveSiteVisitProperty(prop || FEATURED_PROPERTIES[0]);
    setIsSiteVisitModalOpen(true);
  };

  const handleOpenBrochure = (prop?: PropertyItem) => {
    setActiveBrochureProperty(prop || FEATURED_PROPERTIES[0]);
    setIsBrochureModalOpen(true);
  };

  return (
    <div className="bg-slate-950 text-slate-100 min-h-screen">
      {/* Luxury Brand Nav Header */}
      <header className="border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-md sticky top-[45px] z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-amber-500 to-amber-300 flex items-center justify-center text-slate-950 font-black text-lg shadow-md font-heading">
              A
            </div>
            <div>
              <span className="text-base font-extrabold tracking-tight text-white font-heading block leading-none">
                AURA PRIME
              </span>
              <span className="text-[9px] font-bold tracking-widest text-amber-400 uppercase">
                Estates &amp; Private Wealth
              </span>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-6 text-xs font-semibold text-slate-300">
            <a href="#properties" className="hover:text-amber-400 transition-colors">
              Featured Portfolio
            </a>
            <a href="#concierge" className="hover:text-amber-400 transition-colors">
              Chauffeur Concierge
            </a>
            <a href="#yields" className="hover:text-amber-400 transition-colors">
              Investment Yields
            </a>
            <Link
              href="/demos/real-estate/crm"
              className="text-amber-400 hover:text-amber-300 flex items-center gap-1 font-bold"
            >
              <KanbanSquare className="w-3.5 h-3.5" />
              <span>Sales CRM Live View</span>
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => handleOpenSiteVisit()}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-xs shadow-md transition-all hover:scale-[1.02] flex items-center gap-1.5"
            >
              <Calendar className="w-3.5 h-3.5" />
              <span>Schedule Inspection</span>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <RealEstateHero
        cityFilter={cityFilter}
        setCityFilter={setCityFilter}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        priceFilter={priceFilter}
        setPriceFilter={setPriceFilter}
        onOpenSiteVisit={() => handleOpenSiteVisit()}
        currency={currency}
      />

      {/* Commercial Architecture Banner */}
      <DemoCommercialBanner
        title="Deploy This Custom Real Estate CRM & Broker Pipeline for Your Firm"
        description="Eliminate per-seat recurring fees. Ingest leads from Facebook Ads & 99acres within 3 seconds, automate WhatsApp site visit bookings, and manage unit inventories with 100% database sovereignty."
        targetUrl="/real-estate-crm"
        targetLabel="Explore Real Estate CRM & Pricing"
        secondaryUrl="/demos/real-estate/crm"
        secondaryLabel="Open Builder Admin CRM"
        accent="amber"
      />

      {/* Property Grid Section */}
      <section id="properties">
        <PropertyGrid
          cityFilter={cityFilter}
          typeFilter={typeFilter}
          priceFilter={priceFilter}
          currency={currency}
          onSelectSiteVisit={(p) => handleOpenSiteVisit(p)}
          onSelectBrochure={(p) => handleOpenBrochure(p)}
        />
      </section>

      {/* Luxury Concierge & Experience Section */}
      <section id="concierge" className="py-20 bg-slate-900 border-t border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-bold text-amber-400 uppercase tracking-widest block mb-2">
              White-Glove Acquisition Protocol
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-heading">
              A Bespoke Inspection Experience
            </h2>
            <p className="mt-3 text-slate-400 text-sm leading-relaxed">
              We eliminate the friction of real estate acquisitions. From airport tarmac reception to title insurance guarantee, every step is curated for ultra-high-net-worth investors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="p-6 rounded-2xl bg-slate-950 border border-slate-800 shadow-xl relative overflow-hidden group hover:border-amber-500/40 transition-all">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center mb-4">
                <Compass className="w-6 h-6" />
              </div>
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                Phase 01
              </span>
              <h3 className="text-lg font-bold text-white mb-2 font-heading">
                Off-Market Portfolio Curation
              </h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Receive private developer allocations and penthouses not visible on public commercial portals, matched precisely to your residency or rental yield targets.
              </p>
            </div>

            {/* Step 2 */}
            <div className="p-6 rounded-2xl bg-slate-950 border border-slate-800 shadow-xl relative overflow-hidden group hover:border-amber-500/40 transition-all">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center mb-4">
                <Car className="w-6 h-6" />
              </div>
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                Phase 02
              </span>
              <h3 className="text-lg font-bold text-white mb-2 font-heading">
                Chauffeur-Driven Site Viewing
              </h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                A private luxury vehicle receives you at your hotel or residence. Tour multiple signature properties in complete comfort accompanied by a Senior Partner.
              </p>
            </div>

            {/* Step 3 */}
            <div className="p-6 rounded-2xl bg-slate-950 border border-slate-800 shadow-xl relative overflow-hidden group hover:border-amber-500/40 transition-all">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center mb-4">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                Phase 03
              </span>
              <h3 className="text-lg font-bold text-white mb-2 font-heading">
                Escrow &amp; Digital Deed Execution
              </h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Complete legal due diligence, title clearance certificates, RERA guarantees, and digital power-of-attorney registration for seamless global ownership.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CRM Demonstration Callout Banner */}
      <section className="py-14 bg-gradient-to-r from-slate-950 via-amber-950/40 to-slate-950 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="p-8 sm:p-10 rounded-3xl bg-slate-900/90 border border-amber-500/30 shadow-2xl flex flex-col lg:flex-row items-center justify-between gap-6 backdrop-blur-xl">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 text-xs font-bold text-amber-400 uppercase tracking-wider mb-2">
                <Sparkles className="w-4 h-4" />
                <span>Custom Real Estate Software Powered by Johnson Softwares</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-heading">
                Want this exact high-converting property portal &amp; sales CRM for your brokerage?
              </h2>
              <p className="mt-2 text-slate-300 text-sm leading-relaxed">
                Connect your website leads, WhatsApp inquiries, 3D virtual tour bookings, and site visit schedules into a unified automated sales machine.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <Link
                href="/demos/real-estate/crm"
                className="px-5 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-bold text-sm shadow-xl hover:shadow-amber-500/25 flex items-center gap-2 transition-all hover:scale-[1.02]"
              >
                <KanbanSquare className="w-4 h-4" />
                <span>Launch Sales CRM Demo</span>
              </Link>

              <Link
                href="/contact"
                className="px-5 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm border border-slate-700 transition-colors"
              >
                <span>Request Custom Build</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-slate-950 text-slate-400 text-xs border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-slate-300">
            <div className="w-6 h-6 rounded-lg bg-amber-500 flex items-center justify-center text-slate-950 font-black text-xs font-heading">
              A
            </div>
            <span className="font-bold text-white font-heading">Aura Prime Estates</span>
            <span>• Industry Demo by Johnson Softwares</span>
          </div>

          <div className="flex items-center gap-4 text-slate-500">
            <span>RERA Registered Allocations</span>
            <span>•</span>
            <Link href="/demos" className="hover:text-amber-400 transition-colors">
              All Industry Demos
            </Link>
            <span>•</span>
            <Link href="/" className="hover:text-amber-400 transition-colors">
              Johnson Softwares Home
            </Link>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {isSiteVisitModalOpen && (
        <SiteVisitModal
          property={activeSiteVisitProperty}
          currency={currency}
          onClose={() => setIsSiteVisitModalOpen(false)}
        />
      )}

      {isBrochureModalOpen && (
        <BrochureDownloadModal
          property={activeBrochureProperty}
          currency={currency}
          onClose={() => setIsBrochureModalOpen(false)}
        />
      )}
    </div>
  );
}
